// Canvas pipeline for the image editor.
//
// Color adjustments are described as a list of ops ({type, value}) that map 1:1 to
// CSS filter functions. The live preview applies them as a CSS `filter` on the canvas
// element (GPU-accelerated), while export composes the same ops into a single 4x5
// color matrix applied per-pixel, so the saved copy matches the preview in every
// browser without relying on CanvasRenderingContext2D.filter support.

export const ADJUSTMENT_DEFAULTS = {
  brightness: 100,
  contrast: 100,
  saturation: 100
};

// Preset ops are applied before the user's adjustment sliders.
export const FILTER_PRESETS = [
  { id: 'none', ops: [] },
  { id: 'bw', ops: [{ type: 'grayscale', value: 1 }] },
  { id: 'sepia', ops: [{ type: 'sepia', value: 0.65 }] },
  { id: 'vivid', ops: [{ type: 'saturate', value: 1.35 }, { type: 'contrast', value: 1.05 }] },
  { id: 'warm', ops: [{ type: 'sepia', value: 0.25 }, { type: 'saturate', value: 1.25 }] },
  { id: 'cool', ops: [{ type: 'hue-rotate', value: -12 }, { type: 'saturate', value: 1.1 }] },
  { id: 'soft', ops: [{ type: 'contrast', value: 0.92 }, { type: 'brightness', value: 1.05 }, { type: 'saturate', value: 0.85 }] }
];

export function buildOps(adjustments, presetId) {
  const preset = FILTER_PRESETS.find((p) => p.id === presetId);
  const ops = preset ? preset.ops.slice() : [];
  if (adjustments.brightness !== 100)
    ops.push({ type: 'brightness', value: adjustments.brightness / 100 });
  if (adjustments.contrast !== 100)
    ops.push({ type: 'contrast', value: adjustments.contrast / 100 });
  if (adjustments.saturation !== 100)
    ops.push({ type: 'saturate', value: adjustments.saturation / 100 });
  return ops;
}

export function cssFilterForOps(ops) {
  if (!ops.length)
    return 'none';
  return ops
    .map((op) => (op.type === 'hue-rotate' ? `hue-rotate(${op.value}deg)` : `${op.type}(${op.value})`))
    .join(' ');
}

// --- Color matrices (per the CSS filter effects spec, sRGB space) ---
// A transform is {m: 3x3 row-major matrix, o: offset per channel (0..1 scale)}.

function identityTransform() {
  return { m: [1, 0, 0, 0, 1, 0, 0, 0, 1], o: [0, 0, 0] };
}

function transformForOp(op) {
  const v = op.value;
  switch (op.type) {
    case 'brightness':
      return { m: [v, 0, 0, 0, v, 0, 0, 0, v], o: [0, 0, 0] };
    case 'contrast': {
      const off = 0.5 - 0.5 * v;
      return { m: [v, 0, 0, 0, v, 0, 0, 0, v], o: [off, off, off] };
    }
    case 'saturate':
      return saturateMatrix(v);
    case 'grayscale':
      return saturateMatrix(1 - Math.min(1, v));
    case 'sepia':
      return sepiaMatrix(Math.min(1, v));
    case 'hue-rotate':
      return hueRotateMatrix(v);
    default:
      return identityTransform();
  }
}

function saturateMatrix(s) {
  return {
    m: [
      0.213 + 0.787 * s, 0.715 - 0.715 * s, 0.072 - 0.072 * s,
      0.213 - 0.213 * s, 0.715 + 0.285 * s, 0.072 - 0.072 * s,
      0.213 - 0.213 * s, 0.715 - 0.715 * s, 0.072 + 0.928 * s
    ],
    o: [0, 0, 0]
  };
}

function sepiaMatrix(a) {
  const i = 1 - a;
  return {
    m: [
      0.393 * a + i, 0.769 * a, 0.189 * a,
      0.349 * a, 0.686 * a + i, 0.168 * a,
      0.272 * a, 0.534 * a, 0.131 * a + i
    ],
    o: [0, 0, 0]
  };
}

function hueRotateMatrix(degrees) {
  const rad = (degrees * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return {
    m: [
      0.213 + c * 0.787 - s * 0.213, 0.715 - c * 0.715 - s * 0.715, 0.072 - c * 0.072 + s * 0.928,
      0.213 - c * 0.213 + s * 0.143, 0.715 + c * 0.285 + s * 0.140, 0.072 - c * 0.072 - s * 0.283,
      0.213 - c * 0.213 - s * 0.787, 0.715 - c * 0.715 + s * 0.715, 0.072 + c * 0.928 + s * 0.072
    ],
    o: [0, 0, 0]
  };
}

// combined(c) = b(a(c)) => m = b.m * a.m, o = b.m * a.o + b.o
function composeTransforms(a, b) {
  const m = new Array(9);
  for (let row = 0; row < 3; ++row) {
    for (let col = 0; col < 3; ++col) {
      m[row * 3 + col] =
        b.m[row * 3] * a.m[col] +
        b.m[row * 3 + 1] * a.m[3 + col] +
        b.m[row * 3 + 2] * a.m[6 + col];
    }
  }
  const o = new Array(3);
  for (let row = 0; row < 3; ++row) {
    o[row] =
      b.m[row * 3] * a.o[0] +
      b.m[row * 3 + 1] * a.o[1] +
      b.m[row * 3 + 2] * a.o[2] +
      b.o[row];
  }
  return { m: m, o: o };
}

function transformForOps(ops) {
  let t = identityTransform();
  for (const op of ops)
    t = composeTransforms(t, transformForOp(op));
  return t;
}

function applyTransformToCanvas(canvas, t) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imageData.data;
  const m = t.m;
  const o0 = t.o[0] * 255, o1 = t.o[1] * 255, o2 = t.o[2] * 255;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i], g = d[i + 1], b = d[i + 2];
    d[i] = m[0] * r + m[1] * g + m[2] * b + o0;
    d[i + 1] = m[3] * r + m[4] * g + m[5] * b + o1;
    d[i + 2] = m[6] * r + m[7] * g + m[8] * b + o2;
  }
  ctx.putImageData(imageData, 0, 0);
}

// --- Geometry ---

export function orientedSize(img, rotation) {
  return rotation % 180 !== 0
    ? { w: img.height, h: img.width }
    : { w: img.width, h: img.height };
}

export function drawOriented(img, rotation, flipH, flipV) {
  const size = orientedSize(img, rotation);
  const canvas = document.createElement('canvas');
  canvas.width = size.w;
  canvas.height = size.h;
  const ctx = canvas.getContext('2d');
  ctx.translate(size.w / 2, size.h / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);
  return canvas;
}

// Remap a crop rect (in oriented space, pre-change dims w x h) through a view change.
export function rotateRectCW(rect, w, h) {
  return { x: h - (rect.y + rect.h), y: rect.x, w: rect.h, h: rect.w };
}

export function rotateRectCCW(rect, w, h) {
  return { x: rect.y, y: w - (rect.x + rect.w), w: rect.h, h: rect.w };
}

export function flipRectH(rect, w) {
  return { x: w - (rect.x + rect.w), y: rect.y, w: rect.w, h: rect.h };
}

export function flipRectV(rect, h) {
  return { x: rect.x, y: h - (rect.y + rect.h), w: rect.w, h: rect.h };
}

// --- Loading ---

export function loadImage(url) {
  // Freshly uploaded images are processed asynchronously (raws/ -> uploads/), and the
  // processing lambda can lag by 20s+ on cold starts — retry the whole chain with a
  // delay before giving up.
  return attemptLoad(url, 10);
}

function attemptLoad(url, attemptsLeft) {
  // Fetching as a blob keeps the canvas untainted. The CDN may hold a cached
  // variant without CORS headers (a plain <img> request cached before us), so on
  // failure retry with a unique query string to force a fresh origin response,
  // and finally fall back to a crossOrigin image load.
  return fetchToImage(url)
    .catch(() => fetchToImage(cacheBustedUrl(url)))
    .catch(() => loadImageElement(cacheBustedUrl(url), true))
    .catch((error) => {
      if (attemptsLeft <= 1)
        throw error;
      return new Promise((resolve) => setTimeout(resolve, 2000))
        .then(() => attemptLoad(url, attemptsLeft - 1));
    });
}

function cacheBustedUrl(url) {
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'editorcb=' + Date.now();
}

function fetchToImage(url) {
  return fetch(url)
    .then((response) => {
      if (!response.ok)
        throw new Error('HTTP ' + response.status);
      return response.blob();
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      return loadImageElement(objectUrl).finally(() => URL.revokeObjectURL(objectUrl));
    });
}

function loadImageElement(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin)
      img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

// --- Export ---

export function exportBlob(options) {
  const oriented = drawOriented(options.image, options.rotation, options.flipH, options.flipV);
  const crop = options.crop || { x: 0, y: 0, w: oriented.width, h: oriented.height };
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(options.outWidth || crop.w));
  out.height = Math.max(1, Math.round(options.outHeight || crop.h));
  const ctx = out.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(oriented, crop.x, crop.y, crop.w, crop.h, 0, 0, out.width, out.height);

  if (options.ops && options.ops.length)
    applyTransformToCanvas(out, transformForOps(options.ops));

  return new Promise((resolve, reject) => {
    out.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Export failed'))),
      options.mimeType,
      options.quality
    );
  });
}

// Output format: keep lossless formats lossless, everything else becomes JPEG.
export function exportFormatFor(fileName) {
  const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
  switch (ext) {
    case 'png':
    case 'bmp':
    case 'ico':
      return { mimeType: 'image/png', extension: 'png', quality: undefined };
    case 'webp':
      return { mimeType: 'image/webp', extension: 'webp', quality: 0.9 };
    default:
      return { mimeType: 'image/jpeg', extension: 'jpg', quality: 0.9 };
  }
}
